import React, { useCallback, useEffect, useState } from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "@aws-amplify/core";
import ConfirmSignInModal from "../components/login/ConfirmSignInModal";
import { useDisclosure } from "@chakra-ui/react";

export const AuthContext = React.createContext({});

/**
 * The auth context provider
 * 
 * @param {Object} props The component properties
 * @returns The auth context provider
 */
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [action, setAction] = useState("");
  const [username, setUsername] = useState("");

  /**
   * Fetches the current user
   */
  const fetchUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      setUser({
        ...currentUser,
        idToken: session?.tokens?.idToken?.payload,
      });
    } catch (error) {
      console.error(error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const authListener = (data) => {
      switch (data.payload.event) {
        case "signedIn":
          fetchUser();
          break;
        case "signedOut":
          setUser(null);
          break;
        default:
          break;
      }
    };
    const hubListenerCancel = Hub.listen("auth", authListener);

    fetchUser();

    return () => hubListenerCancel();
  }, []);

  /**
   * Confirms the sign in
   * 
   * @param {Object} values The form values
   */
  const onConfirmSignIn = useCallback(
    async (values) =>
      confirmSignIn({
        challengeResponse: values.newPassword,
        options: { authFlowType: "USER_SRP_AUTH" },
      }),
    [],
  );

  /**
   * Signs out the user
   */
  const onSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, []);

  /**
   * Signs in the user
   * 
   * @param {Object} credentials The credentials
   */
  const onSignIn = useCallback(
    async (credentials) => {
      try {
        await onSignOut();
        const { isSignedIn, nextStep } = await signIn({
          ...credentials,
          options: { authFlowType: "USER_SRP_AUTH" },
        });
        if (!isSignedIn) {
          if (
            nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
            || nextStep.signInStep === "RESET_PASSWORD"
          ) {
            if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
              setAction("confirmSignIn")
              setUsername(credentials?.username)
            } else {
              setAction("resetPassword")
              setUsername(credentials?.username)
            }
            onOpen();
          }
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [onOpen, setAction, onSignOut],
  );

  /**
 * Resets the password
 * 
 * @param {Object} values The form values
 */
  const onResetPassword = useCallback(
    async (values) => {
      await confirmResetPassword({
        confirmationCode: values?.confirmationCode,
        username: values?.username,
        newPassword: values?.newPassword,
        options: { authFlowType: "USER_SRP_AUTH" },
      });
      await onSignIn({
        username: values?.username,
        password: values?.newPassword,
      })
    },
    [onSignIn],
  );

  const onPasswordForgotten = useCallback(async (credentials) => {
    await resetPassword({
      username: credentials?.username,
      options: { authFlowType: "USER_SRP_AUTH" },
    })

    setAction("resetPassword")
    setUsername(credentials?.username)
    onOpen()
  }, [onOpen])

  /**
   * Fetches the bearer token
   * 
   * @returns The bearer token
   */
  const fetchBearer = useCallback(async () => {
    try {
      const session = await fetchAuthSession();

      return session?.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        fetchBearer,
        loading,
        confirmSignIn: onConfirmSignIn,
        resetPassword: onResetPassword,
        forgetPassword: onPasswordForgotten,
        signIn: onSignIn,
        signOut: onSignOut,
      }}
    >
      <ConfirmSignInModal action={action} isOpen={isOpen} onClose={onClose} username={username} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
